// CharitiesManager.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import CharityFormModal from "./CharityFormModal";
import DonationFormModal from "./DonationFormModal";
import CharityCategoryManagerModal from "./CharityCategoryManagerModal";
import { applyImageFallback, documentThumbnailUrl } from "../utils/documentPreviews";
import "./CharitiesManager.css";

const ZAR = (n) =>
    new Intl.NumberFormat("en-ZA", {
        style: "currency",
        currency: "ZAR",
    }).format(Number(n || 0));

const centsToRand = (c) => (Number(c || 0) / 100).toFixed(2);
const arrayCount = (value) => Array.isArray(value) ? value.length : 0;
const escapeCsv = (value) => {
    const text = String(value ?? "");
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

// Helper to format dates in SAST as YYYY-MM-DD
const formatDate = (isoString) => {
    if (!isoString) return "";
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return isoString;

    // en-ZA gives "2025/10/11" by default; we normalise to 2025-10-11
    const parts = d
        .toLocaleDateString("en-ZA", { timeZone: "Africa/Johannesburg" })
        .split(/[/.:-]/); // [dd, mm, yyyy]
    if (parts.length !== 3) return isoString;

    const [dd, mm, yyyy] = parts;
    return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
};

export default function CharitiesManager({ onClose }) {
    const [charities, setCharities] = useState([]);
    const [lodges, setLodges] = useState([]); // [{ value, label }]
    const [categories, setCategories] = useState([]); // [{id, name, description}]

    const [selectedCharityId, setSelectedCharityId] = useState(null);
    const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState("all"); // "all" or category_id
    const [reportFrom, setReportFrom] = useState(() => {
        const d = new Date();
        d.setFullYear(d.getFullYear() - 1);
        return d.toISOString().slice(0, 10);
    });
    const [reportTo, setReportTo] = useState(() => new Date().toISOString().slice(0, 10));

    // donations cache: { [charityId]: { rows, loading, loaded } }
    const [donations, setDonations] = useState({});
    const [evidenceDocs, setEvidenceDocs] = useState([]);
    const [evidenceLoading, setEvidenceLoading] = useState(false);

    // modals
    const [charityModal, setCharityModal] = useState(null); // {mode, charity?}
    const [donationModal, setDonationModal] = useState(null); // {charityId, donation?}
    const [categoryModalOpen, setCategoryModalOpen] = useState(false);

    /* ---- helpers for loading / refreshing ---- */

    async function refreshCharities() {
        try {
            const { data } = await axios.get("/api/resources/charities");
            setCharities(data || []);
        } catch (err) {
            console.error("Failed to refresh charities:", {
                message: err.message,
                status: err.response?.status,
                data: err.response?.data,
            });
            alert(
                `Failed to refresh charities: ${
                    err.response?.data?.error || err.message
                }`
            );
        }
    }

    async function refreshAll() {
        try {
            const [cRes, lRes, catRes] = await Promise.all([
                axios.get("/api/resources/charities").catch((err) => {
                    console.error("Error calling /api/resources/charities", {
                        message: err.message,
                        status: err.response?.status,
                        data: err.response?.data,
                    });
                    throw err;
                }),
                axios.get("/api/resources/lodges").catch((err) => {
                    console.error("Error calling /api/resources/lodges", {
                        message: err.message,
                        status: err.response?.status,
                        data: err.response?.data,
                    });
                    throw err;
                }),
                axios.get("/api/resources/charity-categories").catch((err) => {
                    console.error("Error calling /api/resources/charity-categories", {
                        message: err.message,
                        status: err.response?.status,
                        data: err.response?.data,
                    });
                    throw err;
                }),
            ]);

            setCharities(cRes.data || []);
            setLodges(
                (lRes.data || []).map((l) => ({
                    value: l.id,
                    label: l.name,
                }))
            );
            setCategories(catRes.data || []);
        } catch (err) {
            console.error("Failed to load charities, lodges, or types:", {
                message: err.message,
                status: err.response?.status,
                data: err.response?.data,
            });
            alert(
                `Failed to load charities, lodges, or types: ${
                    err.response?.data?.error || err.message
                }`
            );
        }
    }

    /* ---- bootstrap ---- */
    useEffect(() => {
        refreshAll();
    }, []);

    // lock background scroll
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, []);

    /* ---- helpers for category display ---- */
    const getCategoryName = useCallback((charity) => {
        if (!charity) return "";
        if (charity.category_name) return charity.category_name; // if backend joins
        if (charity.category) return charity.category; // legacy text field

        if (charity.category_id && categories.length) {
            const match = categories.find((c) => c.id === charity.category_id);
            if (match) return match.name;
        }
        return "Charity";
    }, [categories]);

    /* ---- derived ---- */
    const filteredCharities = useMemo(() => {
        let list = charities;

        // filter by type/category
        if (typeFilter !== "all") {
            const selectedCat = categories.find(
                (c) => String(c.id) === String(typeFilter)
            );

            list = list.filter((c) => {
                // New schema: category_id matches selected
                if (c.category_id && String(c.category_id) === String(typeFilter)) {
                    return true;
                }

                // Legacy / fallback: compare names
                if (!selectedCat) return false;

                const catName = (getCategoryName(c) || "").toLowerCase();
                const selectedName = (selectedCat.name || "").toLowerCase();
                return catName === selectedName;
            });
        }

        if (search.trim()) {
            const q = search.toLowerCase();
            list = list.filter((c) => {
                const name = (c.name || "").toLowerCase();
                const desc = (c.description || "").toLowerCase();
                const catName = getCategoryName(c).toLowerCase();
                return name.includes(q) || desc.includes(q) || catName.includes(q);
            });
        }

        return list;
    }, [charities, typeFilter, search, categories, getCategoryName]);

    const selectedCharity = useMemo(
        () => charities.find((c) => c.id === selectedCharityId) || null,
        [charities, selectedCharityId]
    );

    const donationState = selectedCharity
        ? donations[selectedCharity.id] || {
        rows: [],
        loading: false,
        loaded: false,
    }
        : { rows: [], loading: false, loaded: false };

    const totalAllForCharity = useMemo(() => {
        if (!selectedCharity) return ZAR(0);
        const d = donations[selectedCharity.id]?.rows || [];
        const sum = d.reduce((a, b) => a + Number(b.amount_cents || 0), 0);
        return ZAR(centsToRand(sum));
    }, [donations, selectedCharity]);

    const byLodge = useMemo(() => {
        if (!selectedCharity) return [];
        const d = donations[selectedCharity.id]?.rows || [];
        const map = new Map();
        d.forEach((r) => {
            const key = r.lodge_id;
            const prev = map.get(key) || { count: 0, total: 0 };
            map.set(key, {
                count: prev.count + 1,
                total: prev.total + Number(r.amount_cents || 0),
            });
        });
        return Array.from(map.entries()).map(([lodgeId, stats]) => ({
            lodgeId,
            count: stats.count,
            total_cents: stats.total,
            lodgeName:
                lodges.find((l) => String(l.value) === String(lodgeId))?.label || "—",
        }));
    }, [donations, lodges, selectedCharity]);

    const reportRows = useMemo(() => {
        return charities.flatMap((charity) => {
            const rows = donations[charity.id]?.rows || [];
            return rows.map((donation) => {
                const lodgeName =
                    lodges.find((l) => String(l.value) === String(donation.lodge_id))?.label || "—";
                const categoryName = getCategoryName(charity);
                return {
                    ...donation,
                    charity_id: charity.id,
                    charity_name: charity.name,
                    category_name: categoryName,
                    lodge_name: lodgeName,
                    amount: Number(donation.amount_cents || 0) / 100,
                    date: formatDate(donation.donated_on),
                };
            });
        });
    }, [charities, donations, lodges, getCategoryName]);

    const filteredReportRows = useMemo(() => {
        return reportRows.filter((row) => {
            if (reportFrom && row.date < reportFrom) return false;
            if (reportTo && row.date > reportTo) return false;
            if (typeFilter !== "all") {
                const charity = charities.find((c) => c.id === row.charity_id);
                if (!charity || String(charity.category_id) !== String(typeFilter)) return false;
            }
            return true;
        });
    }, [charities, reportFrom, reportRows, reportTo, typeFilter]);

    const reportSummary = useMemo(() => {
        const totalCents = filteredReportRows.reduce((sum, row) => sum + Number(row.amount_cents || 0), 0);
        const charityIds = new Set(filteredReportRows.map((row) => row.charity_id));
        const lodgeIds = new Set(filteredReportRows.map((row) => row.lodge_id).filter(Boolean));
        return {
            totalCents,
            donationCount: filteredReportRows.length,
            charityCount: charityIds.size,
            lodgeCount: lodgeIds.size,
        };
    }, [filteredReportRows]);

    const selectedEvidence = useMemo(() => {
        if (!selectedCharity) return [];
        return [
            ...(selectedCharity.photo_document_ids || []).map((id) => ({ id, type: "Photo" })),
            ...(selectedCharity.advert_document_ids || []).map((id) => ({ id, type: "Advert" })),
            ...(selectedCharity.related_document_ids || []).map((id) => ({ id, type: "Document" })),
        ];
    }, [selectedCharity]);

    const fundraiserProgress = useMemo(() => {
        if (!selectedCharity?.target_amount_cents) return null;
        const rows = donations[selectedCharity.id]?.rows || [];
        const total = rows.reduce((sum, row) => sum + Number(row.amount_cents || 0), 0);
        return Math.min(100, Math.round((total / Number(selectedCharity.target_amount_cents)) * 100));
    }, [donations, selectedCharity]);

    const categoryReport = useMemo(() => {
        const map = new Map();
        filteredReportRows.forEach((row) => {
            const key = row.category_name || "Uncategorised";
            const current = map.get(key) || { name: key, count: 0, totalCents: 0 };
            map.set(key, {
                ...current,
                count: current.count + 1,
                totalCents: current.totalCents + Number(row.amount_cents || 0),
            });
        });
        return [...map.values()].sort((a, b) => b.totalCents - a.totalCents || a.name.localeCompare(b.name));
    }, [filteredReportRows]);

    const lodgeReport = useMemo(() => {
        const map = new Map();
        filteredReportRows.forEach((row) => {
            const key = row.lodge_id || "unknown";
            const current = map.get(key) || { id: key, name: row.lodge_name || "—", count: 0, totalCents: 0 };
            map.set(key, {
                ...current,
                count: current.count + 1,
                totalCents: current.totalCents + Number(row.amount_cents || 0),
            });
        });
        return [...map.values()].sort((a, b) => b.totalCents - a.totalCents || a.name.localeCompare(b.name));
    }, [filteredReportRows]);

    const monthlyReport = useMemo(() => {
        const map = new Map();
        filteredReportRows.forEach((row) => {
            const key = (row.date || "").slice(0, 7) || "Unknown";
            const current = map.get(key) || { month: key, count: 0, totalCents: 0 };
            map.set(key, {
                ...current,
                count: current.count + 1,
                totalCents: current.totalCents + Number(row.amount_cents || 0),
            });
        });
        return [...map.values()].sort((a, b) => b.month.localeCompare(a.month));
    }, [filteredReportRows]);

    const exportReportCsv = () => {
        const headers = ["Date", "Charity / Fund", "Type", "Lodge / Entity", "Amount", "Contribution", "Source", "Notes"];
        const rows = filteredReportRows
            .slice()
            .sort((a, b) => b.date.localeCompare(a.date))
            .map((row) => [
                row.date,
                row.charity_name,
                row.category_name,
                row.lodge_name,
                row.amount.toFixed(2),
                row.contribution_summary || "",
                row.source || "",
                row.notes || "",
            ]);
        const csv = [headers, ...rows].map((row) => row.map(escapeCsv).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `charities-report-${reportFrom || "start"}-${reportTo || "end"}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    };

    const buildDownloadUrl = (docOrPath) => {
        if (docOrPath?.id) {
            return `${window.location.origin}/api/documents/${encodeURIComponent(docOrPath.id)}/download`;
        }
        const serverPath = "/opt/websites/freemasonry2/express/documents/";
        const appPath = "/app/documents/";
        const rawPath = docOrPath || "";
        const rel = rawPath.startsWith(serverPath)
            ? rawPath.slice(serverPath.length)
            : rawPath.startsWith(appPath)
                ? rawPath.slice(appPath.length)
                : rawPath;
        const encoded = rel.split("/").map((seg) => encodeURIComponent(seg)).join("/");
        return `${window.location.origin}/documents/${encoded}`;
    };

    const evidenceCounts = useMemo(() => ({
        photos: arrayCount(selectedCharity?.photo_document_ids),
        adverts: arrayCount(selectedCharity?.advert_document_ids),
        documents: arrayCount(selectedCharity?.related_document_ids),
    }), [selectedCharity]);

    const openArchiveDocument = (doc) => {
        if (!doc?.id && !doc?.file_path) return;
        window.open(buildDownloadUrl(doc), "_blank", "noopener,noreferrer");
    };

    /* ---- donations helpers ---- */
    const ensureDonations = async (charityId, force = false) => {
        const current = donations[charityId];
        if (!force && (current?.loaded || current?.loading)) return;

        setDonations((d) => ({
            ...d,
            [charityId]: { rows: current?.rows || [], loading: true, loaded: false },
        }));

        try {
            const { data } = await axios.get(
                `/api/resources/charities/${charityId}/donations`
            );
            setDonations((d) => ({
                ...d,
                [charityId]: { rows: data || [], loading: false, loaded: true },
            }));
        } catch (err) {
            console.error("Failed to load donations:", {
                message: err.message,
                status: err.response?.status,
                data: err.response?.data,
            });
            setDonations((d) => ({
                ...d,
                [charityId]: {
                    rows: current?.rows || [],
                    loading: false,
                    loaded: false,
                },
            }));
            alert(
                `Failed to load donations: ${
                    err.response?.data?.error || err.message
                }`
            );
        }
    };

    const upsertDonationLocal = (charityId, row) => {
        setDonations((prev) => {
            const list = prev[charityId]?.rows || [];
            const exists = list.some((r) => r.id === row.id);
            const rows = exists
                ? list.map((r) => (r.id === row.id ? row : r))
                : [row, ...list];
            return { ...prev, [charityId]: { rows, loading: false, loaded: true } };
        });
    };

    const deleteDonationLocal = (charityId, donationId) => {
        setDonations((prev) => {
            const list = prev[charityId]?.rows || [];
            return {
                ...prev,
                [charityId]: {
                    rows: list.filter((r) => r.id !== donationId),
                    loading: false,
                    loaded: true,
                },
            };
        });
    };

    // NEW: prefetch donations for all charities so the totals on the left
    // populate even before you click a specific charity.
    useEffect(() => {
        if (!charities.length) return;
        charities.forEach((c) => {
            ensureDonations(c.id);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [charities]);

    useEffect(() => {
        if (!selectedCharityId && filteredCharities.length) {
            setSelectedCharityId(filteredCharities[0].id);
        }
    }, [filteredCharities, selectedCharityId]);

    useEffect(() => {
        const loadEvidence = async () => {
            if (!selectedCharityId) {
                setEvidenceDocs([]);
                return;
            }

            setEvidenceLoading(true);
            try {
                const { data } = await axios.get(`/api/resources/charities/${selectedCharityId}/evidence`);
                setEvidenceDocs(data || []);
            } catch (err) {
                console.error("Failed to load charity evidence:", err);
                setEvidenceDocs([]);
            } finally {
                setEvidenceLoading(false);
            }
        };

        loadEvidence();
    }, [selectedCharityId]);

    /* ---- charity CRUD helpers ---- */
    const createCharity = async (payload) => {
        try {
            const { data } = await axios.post("/api/resources/charities", payload);
            await refreshCharities();
            setSelectedCharityId(data.id);
        } catch (err) {
            console.error("Create charity failed:", {
                message: err.message,
                status: err.response?.status,
                data: err.response?.data,
            });
            alert(
                `Saving the charity failed: ${
                    err.response?.data?.error || err.message
                }`
            );
        }
    };

    const updateCharity = async (id, payload) => {
        try {
            await axios.put(`/api/resources/charities/${id}`, payload);
            const prevSelected = selectedCharityId;
            await refreshCharities();
            setSelectedCharityId(prevSelected || id);
        } catch (err) {
            console.error("Update charity failed:", {
                message: err.message,
                status: err.response?.status,
                data: err.response?.data,
            });
            alert(
                `Saving the charity failed: ${
                    err.response?.data?.error || err.message
                }`
            );
        }
    };

    const deleteCharity = async (id) => {
        try {
            await axios.delete(`/api/resources/charities/${id}`);
            await refreshCharities();
            setDonations((d) => {
                const n = { ...d };
                delete n[id];
                return n;
            });
            if (selectedCharityId === id) setSelectedCharityId(null);
        } catch (err) {
            console.error("Delete charity failed:", {
                message: err.message,
                status: err.response?.status,
                data: err.response?.data,
            });
            alert(
                `Delete failed: ${err.response?.data?.error || err.message}`
            );
        }
    };

    /* ---- UI ---- */
    return (
        <div
            className="cm-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cm-title"
            onMouseDown={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className="cm-shell" role="document">
                <header className="cm-header">
                    <h2 id="cm-title">Charities & Foundations</h2>
                    <button className="btn btn-icon" onClick={onClose}>
                        ×
                    </button>
                </header>

                <div className="cm-body">
                    <section className="cm-report-panel">
                        <div className="cm-report-header">
                            <div>
                                <h3>Funds Overview</h3>
                                <p className="muted">Track charitable giving, compare lodge contributions, and export report-ready data.</p>
                            </div>
                            <div className="cm-report-actions">
                                <button
                                    className="btn btn-primary"
                                    type="button"
                                    disabled={!selectedCharity}
                                    onClick={() =>
                                        selectedCharity &&
                                        setDonationModal({
                                            charityId: selectedCharity.id,
                                            donation: null,
                                        })
                                    }
                                >
                                    Capture Donation
                                </button>
                                <button
                                    className="btn"
                                    type="button"
                                    disabled={!filteredReportRows.length}
                                    onClick={exportReportCsv}
                                >
                                    Export CSV
                                </button>
                            </div>
                        </div>

                        <div className="cm-report-filters">
                            <label>
                                <span>From</span>
                                <input
                                    className="inp"
                                    type="date"
                                    value={reportFrom}
                                    onChange={(e) => setReportFrom(e.target.value)}
                                />
                            </label>
                            <label>
                                <span>To</span>
                                <input
                                    className="inp"
                                    type="date"
                                    value={reportTo}
                                    onChange={(e) => setReportTo(e.target.value)}
                                />
                            </label>
                            <button
                                className="btn"
                                type="button"
                                onClick={() => {
                                    setReportFrom("");
                                    setReportTo("");
                                }}
                            >
                                All Dates
                            </button>
                        </div>

                        <div className="cm-report-kpis">
                            <div>
                                <span>Total Given</span>
                                <strong>{ZAR(centsToRand(reportSummary.totalCents))}</strong>
                            </div>
                            <div>
                                <span>Donations</span>
                                <strong>{reportSummary.donationCount}</strong>
                            </div>
                            <div>
                                <span>Funds</span>
                                <strong>{reportSummary.charityCount}</strong>
                            </div>
                            <div>
                                <span>Lodges / Entities</span>
                                <strong>{reportSummary.lodgeCount}</strong>
                            </div>
                        </div>

                        <div className="cm-report-grid">
                            <div className="cm-report-table">
                                <h4>By Type</h4>
                                {!categoryReport.length ? (
                                    <p className="muted">No donations in this report range.</p>
                                ) : (
                                    <table>
                                        <thead>
                                        <tr>
                                            <th>Type</th>
                                            <th>Donations</th>
                                            <th>Total</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {categoryReport.slice(0, 6).map((row) => (
                                            <tr key={row.name}>
                                                <td>{row.name}</td>
                                                <td>{row.count}</td>
                                                <td>{ZAR(centsToRand(row.totalCents))}</td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            <div className="cm-report-table">
                                <h4>By Lodge / Entity</h4>
                                {!lodgeReport.length ? (
                                    <p className="muted">No lodge/entity totals yet.</p>
                                ) : (
                                    <table>
                                        <thead>
                                        <tr>
                                            <th>Lodge / Entity</th>
                                            <th>Donations</th>
                                            <th>Total</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {lodgeReport.slice(0, 6).map((row) => (
                                            <tr key={row.id}>
                                                <td>{row.name}</td>
                                                <td>{row.count}</td>
                                                <td>{ZAR(centsToRand(row.totalCents))}</td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            <div className="cm-report-table">
                                <h4>Recent Months</h4>
                                {!monthlyReport.length ? (
                                    <p className="muted">No monthly totals yet.</p>
                                ) : (
                                    <table>
                                        <thead>
                                        <tr>
                                            <th>Month</th>
                                            <th>Donations</th>
                                            <th>Total</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {monthlyReport.slice(0, 6).map((row) => (
                                            <tr key={row.month}>
                                                <td>{row.month}</td>
                                                <td>{row.count}</td>
                                                <td>{ZAR(centsToRand(row.totalCents))}</td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    </section>

                    <div className="cm-main-grid">
                    {/* LEFT: charities */}
                    <section className="cm-pane cm-pane-left">
                        <div className="cm-pane-header">
                            <h3>Charities</h3>
                            <div style={{ display: "flex", gap: 8 }}>
                                <button
                                    className="btn btn-ghost"
                                    type="button"
                                    onClick={() => setCategoryModalOpen(true)}
                                >
                                    Manage Types
                                </button>
                                <button
                                    className="btn btn-primary"
                                    onClick={() =>
                                        setCharityModal({ mode: "create", charity: null })
                                    }
                                >
                                    Add
                                </button>
                            </div>
                        </div>

                        <div className="cm-filters">
                            <input
                                className="inp"
                                placeholder="Search charities…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                            <select
                                className="inp"
                                value={typeFilter}
                                onChange={(e) => setTypeFilter(e.target.value)}
                            >
                                <option value="all">All types</option>
                                {categories.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {!filteredCharities.length ? (
                            <p className="muted">No charities yet.</p>
                        ) : (
                            <ul className="cm-charity-list">
                                {filteredCharities.map((c) => {
                                    const dState = donations[c.id] || { rows: [] };
                                    const sum = dState.rows.reduce(
                                        (a, b) => a + Number(b.amount_cents || 0),
                                        0
                                    );
                                    const total = ZAR(centsToRand(sum));
                                    const active = selectedCharityId === c.id;
                                    const catName = getCategoryName(c);

                                    return (
                                        <li
                                            key={c.id}
                                            className={
                                                "cm-charity-item" + (active ? " active" : "")
                                            }
                                            onClick={async () => {
                                                setSelectedCharityId(c.id);
                                                await ensureDonations(c.id);
                                            }}
                                        >
                                            <div className="cm-charity-main-row">
                                                <span className="cm-charity-name">{c.name}</span>
                                                <span className="cm-charity-chip">{catName}</span>
                                            </div>
                                            <div className="cm-charity-subrow">
                                                <span>{total} total</span>
                                            </div>
                                            <div
                                                className="cm-charity-actions"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <button
                                                    className="btn btn-sm"
                                                    onClick={() =>
                                                        setCharityModal({ mode: "edit", charity: c })
                                                    }
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-danger"
                                                    onClick={async () => {
                                                        if (!window.confirm("Delete this charity?")) return;
                                                        try {
                                                            await deleteCharity(c.id);
                                                        } catch {
                                                            /* handled in deleteCharity */
                                                        }
                                                    }}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </section>

                    {/* RIGHT: details */}
                    <section className="cm-pane cm-pane-right">
                        {!selectedCharity ? (
                            <p className="muted">
                                Select a charity on the left to view details and manage
                                donations.
                            </p>
                        ) : (
                            <>
                                <div className="cm-pane-header">
                                    <div>
                                        <h3>{selectedCharity.name}</h3>
                                        <div className="cm-activity-meta">
                                            <span>{getCategoryName(selectedCharity)}</span>
                                            <span>{selectedCharity.status || "Active"}</span>
                                            {selectedCharity.activity_date && <span>{formatDate(selectedCharity.activity_date)}</span>}
                                            {selectedCharity.event_name && <span>{selectedCharity.event_name}</span>}
                                            {selectedCharity.venue && <span>{selectedCharity.venue}</span>}
                                        </div>
                                        <p className="cm-charity-desc">
                                            {selectedCharity.description || "No description."}
                                        </p>
                                        {selectedCharity.website && (
                                            <a
                                                href={selectedCharity.website}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                {selectedCharity.website}
                                            </a>
                                        )}
                                    </div>
                                    <div className="cm-don-summary">
                    <span className="chip">
                      Total: {totalAllForCharity}
                    </span>
                                        <span className="chip">
                      Donations: {donationState.rows.length}
                    </span>
                                        <span className="chip">
                      Evidence: {selectedEvidence.length}
                    </span>
                                    </div>
                                </div>

                                <div className="cm-info-grid">
                                    <div>
                                        <span>Event / Activity</span>
                                        <strong>{selectedCharity.event_name || "Not specified"}</strong>
                                    </div>
                                    <div>
                                        <span>Contact Person</span>
                                        <strong>{selectedCharity.contact_person || "Not captured"}</strong>
                                    </div>
                                    <div>
                                        <span>Contact Details</span>
                                        <strong>{selectedCharity.contact_details || "Not captured"}</strong>
                                    </div>
                                    <div>
                                        <span>Contribution</span>
                                        <strong>{selectedCharity.contribution_summary || totalAllForCharity}</strong>
                                    </div>
                                </div>

                                <div className="cm-activity-grid">
                                    <div>
                                        <span>Target</span>
                                        <strong>{selectedCharity.target_amount_cents ? ZAR(centsToRand(selectedCharity.target_amount_cents)) : "Not set"}</strong>
                                    </div>
                                    <div>
                                        <span>Progress</span>
                                        <strong>{fundraiserProgress === null ? "—" : `${fundraiserProgress}%`}</strong>
                                    </div>
                                    <div>
                                        <span>Photos</span>
                                        <strong>{evidenceCounts.photos}</strong>
                                    </div>
                                    <div>
                                        <span>Attachments</span>
                                        <strong>{evidenceCounts.adverts + evidenceCounts.documents}</strong>
                                    </div>
                                </div>

                                {(selectedCharity.outcome || selectedCharity.campaign_notes) && (
                                    <div className="cm-activity-notes">
                                        {selectedCharity.outcome && (
                                            <section>
                                                <h4>Outcome / Impact</h4>
                                                <p>{selectedCharity.outcome}</p>
                                            </section>
                                        )}
                                        {selectedCharity.campaign_notes && (
                                            <section>
                                                <h4>Activity Notes</h4>
                                                <p>{selectedCharity.campaign_notes}</p>
                                            </section>
                                        )}
                                    </div>
                                )}

                                <div className="cm-evidence-panel">
                                    <div className="cm-don-header">
                                        <h4>Photos, Adverts & Evidence</h4>
                                        <button
                                            className="btn"
                                            type="button"
                                            onClick={() => setCharityModal({ mode: "edit", charity: selectedCharity })}
                                        >
                                            Link Evidence
                                        </button>
                                    </div>
                                    {evidenceLoading ? (
                                        <p className="muted">Loading evidence…</p>
                                    ) : !selectedEvidence.length ? (
                                        <p className="muted">No photos, adverts, or archive documents linked yet.</p>
                                    ) : !evidenceDocs.length ? (
                                        <p className="muted">Evidence IDs are recorded, but no matching archive documents were found.</p>
                                    ) : (
                                        <div className="cm-evidence-list">
                                            {evidenceDocs.map((doc) => (
                                                <article key={`${doc.evidence_type}-${doc.id}`}>
                                                    <div className="cm-evidence-preview">
                                                        {doc.file_path && (
                                                            <img
                                                                src={documentThumbnailUrl(doc)}
                                                                alt=""
                                                                loading="lazy"
                                                                onError={(event) => applyImageFallback(event, doc)}
                                                            />
                                                        )}
                                                    </div>
                                                    <div className="cm-evidence-content">
                                                        <span>{doc.evidence_type}</span>
                                                        <strong>{doc.title || doc.filename || doc.id}</strong>
                                                        <p>{doc.document_type_description || "Archive document"} · {doc.file_type_description || "File"} · {formatDate(doc.date_created)}</p>
                                                        {doc.description && <p>{doc.description}</p>}
                                                        <button className="btn btn-sm" type="button" onClick={() => openArchiveDocument(doc)}>
                                                            Open / Download
                                                        </button>
                                                    </div>
                                                </article>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <h4>By Lodge / Entity</h4>
                                {!byLodge.length ? (
                                    <p className="muted">No donations yet.</p>
                                ) : (
                                    <table className="cm-mini-table">
                                        <thead>
                                        <tr>
                                            <th>Lodge / Entity</th>
                                            <th>Donations</th>
                                            <th>Total</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {byLodge.map((row) => (
                                            <tr key={row.lodgeId}>
                                                <td>{row.lodgeName}</td>
                                                <td>{row.count}</td>
                                                <td>{ZAR(centsToRand(row.total_cents))}</td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                )}

                                <div className="cm-don-header">
                                    <h4>All Donations</h4>
                                    <button
                                        className="btn"
                                        onClick={() =>
                                            setDonationModal({
                                                charityId: selectedCharity.id,
                                                donation: null,
                                            })
                                        }
                                    >
                                        Add Donation
                                    </button>
                                </div>

                                <div className="don-list">
                                    {donationState.loading ? (
                                        <p className="muted">Loading…</p>
                                    ) : !donationState.rows.length ? (
                                        <p className="muted">No donations yet.</p>
                                    ) : (
                                        donationState.rows
                                            .slice()
                                            .sort((a, b) =>
                                                a.donated_on < b.donated_on ? 1 : -1
                                            )
                                            .map((r) => {
                                                const lodgeName =
                                                    lodges.find(
                                                        (l) => String(l.value) === String(r.lodge_id)
                                                    )?.label || "—";
                                                return (
                                                    <div className="don-row" key={r.id}>
                                                        <span>{formatDate(r.donated_on)}</span>
                                                        <span className="don-lodge">{lodgeName}</span>
                                                        <span className="don-amt">
                              {Number(r.amount_cents || 0) > 0 ? ZAR(centsToRand(r.amount_cents)) : "—"}
                            </span>
                                                        <span className="don-contribution">
                              {r.contribution_summary || ""}
                            </span>
                                                        <span className="don-src">
                              {r.source || "—"}
                            </span>
                                                        <span className="don-notes">
                              {r.notes || ""}
                            </span>
                                                        <span className="don-actions">
                              <button
                                  className="btn btn-sm"
                                  onClick={() =>
                                      setDonationModal({
                                          charityId: selectedCharity.id,
                                          donation: r,
                                      })
                                  }
                              >
                                Edit
                              </button>
                              <button
                                  className="btn btn-sm btn-danger"
                                  onClick={async () => {
                                      if (
                                          !window.confirm(
                                              "Delete this donation?"
                                          )
                                      )
                                          return;
                                      try {
                                          await axios.delete(
                                              `/api/resources/charities/${selectedCharity.id}/donations/${r.id}`
                                          );
                                          deleteDonationLocal(
                                              selectedCharity.id,
                                              r.id
                                          );
                                      } catch (err) {
                                          console.error(
                                              "Delete donation failed:",
                                              {
                                                  message: err.message,
                                                  status: err.response?.status,
                                                  data: err.response?.data,
                                              }
                                          );
                                          alert(
                                              `Delete failed: ${
                                                  err.response?.data?.error ||
                                                  err.message
                                              }`
                                          );
                                      }
                                  }}
                              >
                                Delete
                              </button>
                            </span>
                                                    </div>
                                                );
                                            })
                                    )}
                                </div>
                            </>
                        )}
                    </section>
                    </div>
                </div>
            </div>

            {/* Charity editor modal */}
            {charityModal && (
                <CharityFormModal
                    mode={charityModal.mode}
                    charity={charityModal.charity}
                    onCancel={() => setCharityModal(null)}
                    onCreate={async (payload) => {
                        await createCharity(payload);
                        setCharityModal(null);
                    }}
                    onSave={async (id, payload) => {
                        await updateCharity(id, payload);
                        setCharityModal(null);
                    }}
                />
            )}

            {/* Donation editor modal */}
            {donationModal && (
                <DonationFormModal
                    charityId={donationModal.charityId}
                    donation={donationModal.donation}
                    lodges={lodges}
                    onCancel={() => setDonationModal(null)}
                    onSaved={async (row) => {
                        upsertDonationLocal(donationModal.charityId, row);
                        await ensureDonations(donationModal.charityId, true);
                        setDonationModal(null);
                    }}
                />
            )}

            {/* Category manager modal */}
            {categoryModalOpen && (
                <CharityCategoryManagerModal
                    initialCategories={categories}
                    onClose={() => setCategoryModalOpen(false)}
                    onUpdated={(newCats) => {
                        setCategories(newCats);
                    }}
                />
            )}
        </div>
    );
}
